/**
 * useMutationImageUpload
 *
 * Hook for mutation batch image uploads using presigned S3 URLs.
 * Follows the same pattern as useAssetUpload (launchpad) but simplified:
 * - No folder structure required — just flat image files
 * - No metadata registration — CSV already defined the mapping
 * - Matches selected files against required filenames from CSV
 */

import { useRef, useState, useCallback } from 'react';
import {
  getPresignedMutationImageUrls,
  uploadFileToS3,
  confirmBatchImages,
  type PresignedImageUrl,
} from '../api/mutations';

export type UploadPhase =
  | 'idle'
  | 'matching'
  | 'uploading'
  | 'confirming'
  | 'done'
  | 'error';

export interface UploadProgress {
  phase: UploadPhase;
  statusMessage: string;
  totalItems: number;
  completedItems: number;
  failedItems: number;
  percent: number;
}

export interface MatchResult {
  matched: Map<string, File>;
  missing: string[];
  orphaned: string[];
}

/** Map file extension to MIME content type */
const EXT_TO_CONTENT_TYPE: Record<string, string> = {
  'png': 'image/png',
  'jpg': 'image/jpeg',
  'jpeg': 'image/jpeg',
  'gif': 'image/gif',
  'webp': 'image/webp',
  'glb': 'model/gltf-binary',
};

interface UseMutationImageUploadOptions {
  projectSlug: string | null;
  collectionSlug: string;
  batchId: string;
  requiredFilenames: string[];
  onComplete?: (result: { uploaded: number; total: number }) => void;
  onError?: (error: string) => void;
}

export function useMutationImageUpload({
  projectSlug,
  collectionSlug,
  batchId,
  requiredFilenames,
  onComplete,
  onError,
}: UseMutationImageUploadOptions) {
  const [progress, setProgress] = useState<UploadProgress>({
    phase: 'idle',
    statusMessage: '',
    totalItems: 0,
    completedItems: 0,
    failedItems: 0,
    percent: 0,
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [matchResult, setMatchResult] = useState<MatchResult | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef(false);

  const updateProgress = useCallback((updates: Partial<UploadProgress>) => {
    setProgress(prev => {
      const next = { ...prev, ...updates };
      if (next.totalItems > 0 && next.phase === 'uploading') {
        next.percent = Math.round(((next.completedItems + next.failedItems) / next.totalItems) * 100);
      }
      return next;
    });
  }, []);

  /**
   * Read all entries from a FileSystemDirectoryReader
   */
  const readAllEntries = (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> => {
    return new Promise((resolve, reject) => {
      const allEntries: FileSystemEntry[] = [];
      const readBatch = () => {
        reader.readEntries(
          (entries) => {
            if (entries.length === 0) {
              resolve(allEntries);
            } else {
              allEntries.push(...entries);
              readBatch();
            }
          },
          reject
        );
      };
      readBatch();
    });
  };

  /**
   * Recursively collect files from a dropped directory entry
   */
  const collectFilesFromEntry = async (entry: FileSystemEntry): Promise<File[]> => {
    const files: File[] = [];
    if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry;
      const file = await new Promise<File>((resolve) => {
        fileEntry.file(resolve);
      });
      files.push(file);
    } else if (entry.isDirectory) {
      const dirEntry = entry as FileSystemDirectoryEntry;
      const reader = dirEntry.createReader();
      const entries = await readAllEntries(reader);
      for (const childEntry of entries) {
        const childFiles = await collectFilesFromEntry(childEntry);
        files.push(...childFiles);
      }
    }
    return files;
  };

  /**
   * Match selected files against required filenames from the CSV
   */
  const matchFiles = useCallback((files: File[]): MatchResult => {
    const requiredSet = new Set(requiredFilenames);
    const matched = new Map<string, File>();
    const orphaned: string[] = [];

    for (const file of files) {
      if (requiredSet.has(file.name)) {
        matched.set(file.name, file);
      } else {
        orphaned.push(file.name);
      }
    }

    const missing = requiredFilenames.filter(f => !matched.has(f));

    return { matched, missing, orphaned };
  }, [requiredFilenames]);

  /**
   * Handle file selection (from input or drop). Matches files and stores them.
   */
  const selectFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);

    // Filter to only image files
    const imageFiles = fileArray.filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return ext && EXT_TO_CONTENT_TYPE[ext];
    });

    setSelectedFiles(imageFiles);
    const result = matchFiles(imageFiles);
    setMatchResult(result);
    setErrors([]);
  }, [matchFiles]);

  /**
   * Upload matched files to S3 via presigned URLs
   */
  const startUpload = useCallback(async () => {
    if (!matchResult || matchResult.matched.size === 0) return;

    abortRef.current = false;
    setIsUploading(true);
    setErrors([]);

    const items = Array.from(matchResult.matched.entries());
    const totalCount = items.length;

    try {
      // Phase 1: Get presigned URLs
      updateProgress({
        phase: 'uploading',
        statusMessage: 'Getting upload URLs...',
        totalItems: totalCount,
        completedItems: 0,
        failedItems: 0,
        percent: 0,
      });

      const PRESIGN_BATCH_SIZE = 500;
      const urlMap = new Map<string, PresignedImageUrl>();

      for (let i = 0; i < items.length; i += PRESIGN_BATCH_SIZE) {
        if (abortRef.current) break;

        const batch = items.slice(i, i + PRESIGN_BATCH_SIZE);
        const presignItems = batch.map(([filename, file]) => {
          const ext = filename.split('.').pop()?.toLowerCase() || 'png';
          return { filename, contentType: EXT_TO_CONTENT_TYPE[ext] || 'image/png' };
        });

        try {
          const urls = await getPresignedMutationImageUrls(
            projectSlug, collectionSlug, batchId, presignItems
          );
          for (const url of urls) {
            urlMap.set(url.filename, url);
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          setErrors(prev => [...prev, `Failed to get upload URLs: ${msg}`]);
          updateProgress({ phase: 'error', statusMessage: `Failed to get upload URLs` });
          setIsUploading(false);
          onError?.(`Failed to get upload URLs: ${msg}`);
          return;
        }
      }

      if (abortRef.current) {
        setIsUploading(false);
        return;
      }

      // Phase 2: Upload to S3 with concurrency
      const CONCURRENCY = 30;
      let completed = 0;
      let failedCount = 0;
      const uploadErrors: string[] = [];
      const successfulUploads: { filename: string; s3Key: string }[] = [];

      const itemsToUpload = items.filter(([filename]) => urlMap.has(filename));
      let idx = 0;

      const worker = async () => {
        while (idx < itemsToUpload.length) {
          if (abortRef.current) return;

          const currentIdx = idx++;
          const entry = itemsToUpload[currentIdx];
          if (!entry) break;

          const [filename, file] = entry;
          const urlInfo = urlMap.get(filename);
          if (!urlInfo) continue;

          const ext = filename.split('.').pop()?.toLowerCase() || 'png';
          const contentType = EXT_TO_CONTENT_TYPE[ext] || 'image/png';

          try {
            await uploadFileToS3(urlInfo.uploadUrl, file, contentType);
            successfulUploads.push({ filename, s3Key: urlInfo.s3Key });
            completed++;
          } catch (err) {
            failedCount++;
            uploadErrors.push(`${filename}: ${err instanceof Error ? err.message : String(err)}`);
          }

          updateProgress({
            completedItems: completed,
            failedItems: failedCount,
            statusMessage: `Uploading ${completed + failedCount} of ${totalCount}${failedCount > 0 ? ` (${failedCount} failed)` : ''}`,
          });
        }
      };

      const workers = Array.from(
        { length: Math.min(CONCURRENCY, itemsToUpload.length) },
        () => worker()
      );
      await Promise.all(workers);

      if (abortRef.current) {
        setIsUploading(false);
        return;
      }

      // Phase 3: Confirm uploads with backend
      if (successfulUploads.length > 0) {
        updateProgress({
          phase: 'confirming',
          statusMessage: 'Confirming uploads...',
        });

        try {
          await confirmBatchImages(projectSlug, collectionSlug, batchId, successfulUploads);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          uploadErrors.push(`Confirmation failed: ${msg}`);
        }
      }

      // Done
      if (uploadErrors.length > 0) {
        setErrors(uploadErrors);
      }

      updateProgress({
        phase: failedCount > 0 ? 'error' : 'done',
        statusMessage: failedCount > 0
          ? `${completed} uploaded, ${failedCount} failed`
          : `${completed} images uploaded successfully`,
        completedItems: completed,
        failedItems: failedCount,
        percent: 100,
      });

      if (failedCount === 0) {
        onComplete?.({ uploaded: completed, total: totalCount });
      } else {
        onError?.(`${failedCount} of ${totalCount} uploads failed`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrors([msg]);
      updateProgress({ phase: 'error', statusMessage: msg });
      onError?.(msg);
    } finally {
      setIsUploading(false);
    }
  }, [matchResult, projectSlug, collectionSlug, batchId, updateProgress, onComplete, onError]);

  /**
   * Cancel in-progress uploads
   */
  const cancelUpload = useCallback(() => {
    abortRef.current = true;
  }, []);

  /**
   * Reset to allow re-selection
   */
  const reset = useCallback(() => {
    setSelectedFiles([]);
    setMatchResult(null);
    setErrors([]);
    setProgress({
      phase: 'idle',
      statusMessage: '',
      totalItems: 0,
      completedItems: 0,
      failedItems: 0,
      percent: 0,
    });
    setIsUploading(false);
    abortRef.current = false;
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  /**
   * Handle drop event — supports both individual files and folders
   */
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const items = e.dataTransfer.items;
    const collectedFiles: File[] = [];

    if (items && items.length > 0) {
      // Check if any entry is a directory
      const entries: FileSystemEntry[] = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) entries.push(entry);
      }

      if (entries.length > 0) {
        for (const entry of entries) {
          const files = await collectFilesFromEntry(entry);
          collectedFiles.push(...files);
        }
      }
    }

    if (collectedFiles.length === 0) {
      // Fallback: use dataTransfer.files
      collectedFiles.push(...Array.from(e.dataTransfer.files));
    }

    if (collectedFiles.length > 0) {
      selectFiles(collectedFiles);
    }
  }, [selectFiles]);

  return {
    progress,
    errors,
    isUploading,
    matchResult,
    selectedFiles,
    inputRef,
    selectFiles,
    startUpload,
    cancelUpload,
    reset,
    handleDrop,
  };
}
